import { Tag } from 'antd';
import { MobileOutlined, PhoneOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useRef } from 'react';
import { OwnerStatus } from '../../utils/constants/tagColors';
import { useListResourceArchiving } from '../../utils/listResourceArchiving';
import { postalCodeFilter } from './utils/ownersFilters';
import { PersonCell, TwoLinesCell } from '../../components/ListCells/ListCells';
import { statusSorter } from '../../utils/sorterMethods';
import { createActionColumn } from '../../utils/createActionColumn';
import { createListHeaders } from '../../utils/createListHeaders';
import { formatPhoneNumber } from '../../utils/formatPhoneNumber';

/**
 * Custom hook that returns columns and headers for the owners table.
 * @hook
 * @param {Function} setForceRefresh - Function to force refresh the table.
 * @returns {Object} - Object containing columns and headers.
 */
export const useColumns = (setForceRefresh) => {
  const { t } = useTranslation();
  const { deleteResource, restoreResource } = useListResourceArchiving(
    setForceRefresh,
    'owners/archiving'
  );

  const searchInput = useRef(null);
  const columns = [
    {
      title: t('owners.form.reference'),
      key: 'reference',
      dataIndex: 'reference',
      sorter: true
    },
    {
      title: t('owners.title'),
      key: 'last_name',
      render: (record) => (
        <PersonCell
          firstName={record?.first_name}
          lastName={record?.last_name}
          name={`${record?.last_name || ''} ${record?.first_name || ''}`}
          subtitle={record?.email}
        />
      ),
      sorter: true
    },
    {
      title: t('owners.form.address.city'),
      key: 'address.postal_code',
      dataIndex: 'address',
      ...postalCodeFilter(searchInput, t),
      render: (address) => (
        <TwoLinesCell
          main={address?.city}
          sub={[address?.postal_code, address?.state]
            .filter(Boolean)
            .join(' · ')}
        />
      ),
      sorter: true
    },
    {
      title: t('owners.form.phone_number_full'),
      key: 'cell_phone_number',
      render: (record) => (
        <div className="cell-lines">
          <span className="cell-sub">
            <MobileOutlined />{' '}
            {formatPhoneNumber(record?.cell_phone_number) || '-'}
          </span>
          <span className="cell-sub">
            <PhoneOutlined /> {formatPhoneNumber(record?.phone_number) || '-'}
          </span>
        </div>
      ),
      sorter: true
    },
    {
      title: t('owners.form.status'),
      key: 'status',
      dataIndex: ['status'],
      render: (status) =>
        status ? (
          <Tag color={OwnerStatus[status]}>{t(`owners.tags.${status}`)}</Tag>
        ) : (
          'N/R'
        ),
      sorter: (a, b) => statusSorter(a, b, 'owners.tags', t)
    },
    createActionColumn({
      t,
      deleteResource,
      restoreResource,
      resourcePath: 'owners'
    })
  ];

  const headers = createListHeaders({ statusKey: 'owner_status' });

  return { columns, headers };
};
